import { useEffect, useState } from "react";
import {
  AssessmentSafety,
  getAssessmentSafety,
  listManifests,
  ManifestRow,
} from "@/api/methodologyConsole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ReportClassificationBanner from "@/components/report/ReportClassificationBanner";
import {
  AdvancedJson,
  EmptyState,
  ErrorState,
  LoadingState,
  ReadinessCard,
  ReasonCodes,
} from "./primitives";

/**
 * Shared surface for Report Safety and Governance & Eligibility.
 * Every decision shown here is produced server-side; nothing is derived
 * in the browser and no eligibility can be granted from this screen.
 */
export default function AssessmentSafetyTab({
  mode,
}: {
  mode: "report" | "governance";
}) {
  const [assessments, setAssessments] = useState<ManifestRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [safety, setSafety] = useState<AssessmentSafety | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listManifests(100, 0)
      .then((rows) => {
        setAssessments(rows);
        setSelected(rows[0]?.assessment_id ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setSafety(null);
    getAssessmentSafety(selected)
      .then(setSafety)
      .catch((e: Error) => setError(e.message));
  }, [selected]);

  if (error) return <ErrorState message={error} />;
  if (!assessments) return <LoadingState label="Loading assessments…" />;
  if (assessments.length === 0)
    return (
      <EmptyState
        title="No assessments available"
        description="No assessment with a manifest record exists in this environment."
      />
    );

  const gp = safety?.governance_profile as Record<string, any> | null;

  return (
    <div className="space-y-6">
      <Select value={selected ?? undefined} onValueChange={setSelected}>
        <SelectTrigger className="w-96" aria-label="Select assessment">
          <SelectValue placeholder="Select an assessment" />
        </SelectTrigger>
        <SelectContent>
          {assessments.map((a) => (
            <SelectItem key={a.id} value={a.assessment_id}>
              {a.portfolio_name ?? "Unnamed"} · {a.questionnaire_id ?? "no questionnaire"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!safety ? (
        <LoadingState label="Loading server safety decisions…" />
      ) : mode === "report" ? (
        <div className="space-y-4">
          <ReportClassificationBanner
            classification={safety.report_classification}
            watermarkRequired={safety.watermark_required}
          />

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Final report action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                disabled={
                  String(safety.report_classification?.classification ?? "") !== "final"
                }
                title="Final report generation requires server-confirmed final readiness"
              >
                Generate final report
              </Button>
              {String(safety.report_classification?.classification ?? "") !== "final" && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Disabled — the server has not classified this report as final.
                  </p>
                  <ReasonCodes
                    codes={
                      (safety.report_classification?.reason_codes as string[]) ?? [
                        "final_report_not_allowed",
                      ]
                    }
                  />
                </>
              )}
              <p className="text-xs text-muted-foreground">
                Composed draft reports render a non-suppressible watermark on the cover, on
                every page, in the PDF metadata and in the report manifest.
              </p>
            </CardContent>
          </Card>

          <details>
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Advanced — server classification and lineage
            </summary>
            <div className="mt-2">
              <AdvancedJson
                data={{
                  classification: safety.report_classification,
                  lineage: safety.lineage,
                }}
              />
            </div>
          </details>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <ReadinessCard
              title="Client launch"
              ready={Boolean(safety.launch_readiness?.ready)}
              codes={safety.launch_readiness?.reason_codes as string[]}
            />
            <ReadinessCard
              title="Final report"
              ready={
                String(safety.report_classification?.classification ?? "") === "final"
              }
              codes={safety.report_classification?.reason_codes as string[]}
            />
            <ReadinessCard
              title="Anonymous benchmarking"
              ready={Boolean(
                safety.benchmark_eligibility?.eligible ?? safety.benchmark_eligibility?.ready,
              )}
              codes={safety.benchmark_eligibility?.reason_codes as string[]}
            />
            <ReadinessCard
              title="AI-assisted analysis"
              ready={Boolean(safety.ai_eligibility?.eligible ?? safety.ai_eligibility?.ready)}
              codes={safety.ai_eligibility?.reason_codes as string[]}
            />
            <ReadinessCard
              title="AI training"
              ready={Boolean(gp?.ai_training_allowed) && Boolean(safety.ai_eligibility?.eligible)}
              codes={["ai_training_denied_by_default"]}
            />
            <ReadinessCard
              title="Commercial intelligence"
              ready={Boolean(gp?.commercial_intelligence_allowed)}
              codes={["commercial_intelligence_denied_by_default"]}
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Governance profile</CardTitle>
            </CardHeader>
            <CardContent>
              {!gp ? (
                <EmptyState
                  title="No governance profile bound"
                  description="This assessment has no governance profile recorded, so every optional use is denied by default."
                />
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    ["Tenant-only use", gp.tenant_only],
                    ["Portfolio aggregation", gp.portfolio_aggregation_allowed],
                    ["Anonymous benchmarking", gp.anonymous_benchmarking_allowed],
                    ["AI-assisted analysis", gp.ai_assisted_analysis_allowed],
                    ["AI training", gp.ai_training_allowed],
                    ["Commercial intelligence", gp.commercial_intelligence_allowed],
                  ].map(([label, value]) => (
                    <div key={String(label)} className="flex items-center justify-between border-b py-1">
                      <span className="text-sm">{String(label)}</span>
                      <Badge variant={value ? "default" : "secondary"}>
                        {value ? "Allowed" : "Denied"}
                      </Badge>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-b py-1">
                    <span className="text-sm">Retention</span>
                    <span className="text-sm">
                      {gp.retention_months ? `${gp.retention_months} months` : "Not set"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b py-1">
                    <span className="text-sm">Anonymization threshold (k)</span>
                    <span className="text-sm">{gp.min_anonymization_k ?? "Not set"}</span>
                  </div>
                  <div className="flex items-center justify-between border-b py-1">
                    <span className="text-sm">Minimum cohort size</span>
                    <span className="text-sm">{gp.min_cohort_size ?? "Not set"}</span>
                  </div>
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                Governance decisions are read-only in this phase. External model sharing and
                legal hold remain denied unless explicitly recorded on the profile.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
